import React, { useState } from 'react';
import { Calculator, ArrowLeft, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

// Age-adjusted body fat categories (Jackson-Pollock / ACE reference)
const maleCategories = {
  '18-29': [
    { label: 'Essential Fat', max: 5, color: 'text-blue-600' },
    { label: 'Excellent', max: 13, color: 'text-green-600' },
    { label: 'Good', max: 17, color: 'text-[#C9A962]' },
    { label: 'Average', max: 22, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
  '30-39': [
    { label: 'Essential Fat', max: 5, color: 'text-blue-600' },
    { label: 'Excellent', max: 14, color: 'text-green-600' },
    { label: 'Good', max: 18, color: 'text-[#C9A962]' },
    { label: 'Average', max: 23, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
  '40-49': [
    { label: 'Essential Fat', max: 5, color: 'text-blue-600' },
    { label: 'Excellent', max: 16, color: 'text-green-600' },
    { label: 'Good', max: 20, color: 'text-[#C9A962]' },
    { label: 'Average', max: 25, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
  '50-59': [
    { label: 'Essential Fat', max: 5, color: 'text-blue-600' },
    { label: 'Excellent', max: 17, color: 'text-green-600' },
    { label: 'Good', max: 21, color: 'text-[#C9A962]' },
    { label: 'Average', max: 27, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
  '60+': [
    { label: 'Essential Fat', max: 5, color: 'text-blue-600' },
    { label: 'Excellent', max: 18, color: 'text-green-600' },
    { label: 'Good', max: 22, color: 'text-[#C9A962]' },
    { label: 'Average', max: 28, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
};

const femaleCategories = {
  '18-29': [
    { label: 'Essential Fat', max: 13, color: 'text-blue-600' },
    { label: 'Excellent', max: 20, color: 'text-green-600' },
    { label: 'Good', max: 24, color: 'text-[#C9A962]' },
    { label: 'Average', max: 31, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
  '30-39': [
    { label: 'Essential Fat', max: 13, color: 'text-blue-600' },
    { label: 'Excellent', max: 21, color: 'text-green-600' },
    { label: 'Good', max: 25, color: 'text-[#C9A962]' },
    { label: 'Average', max: 32, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
  '40-49': [
    { label: 'Essential Fat', max: 13, color: 'text-blue-600' },
    { label: 'Excellent', max: 23, color: 'text-green-600' },
    { label: 'Good', max: 27, color: 'text-[#C9A962]' },
    { label: 'Average', max: 34, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
  '50-59': [
    { label: 'Essential Fat', max: 13, color: 'text-blue-600' },
    { label: 'Excellent', max: 24, color: 'text-green-600' },
    { label: 'Good', max: 28, color: 'text-[#C9A962]' },
    { label: 'Average', max: 35, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
  '60+': [
    { label: 'Essential Fat', max: 13, color: 'text-blue-600' },
    { label: 'Excellent', max: 25, color: 'text-green-600' },
    { label: 'Good', max: 29, color: 'text-[#C9A962]' },
    { label: 'Average', max: 36, color: 'text-orange-500' },
    { label: 'Above Average', max: Infinity, color: 'text-red-500' },
  ],
};

const getAgeGroup = (age) => {
  if (age < 30) return '18-29';
  if (age < 40) return '30-39';
  if (age < 50) return '40-49';
  if (age < 60) return '50-59';
  return '60+';
};

const getCategory = (bf, gender, age) => {
  const ageGroup = getAgeGroup(age);
  const cats = gender === 'male' ? maleCategories[ageGroup] : femaleCategories[ageGroup];
  for (const cat of cats) {
    if (bf <= cat.max) return { label: cat.label, color: cat.color, ageGroup };
  }
  return { label: 'Above Average', color: 'text-red-500', ageGroup };
};

const BodyFatCalculator = () => {
  const [gender, setGender] = useState('male');
  const [unit, setUnit] = useState('metric');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [measurements, setMeasurements] = useState({
    height: '',
    neck: '',
    waist: '',
    hip: ''
  });
  const [result, setResult] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMeasurements(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const cmToInches = (cm) => cm / 2.54;
  const kgToLbs = (kg) => kg * 2.20462;

  const calculateBodyFat = () => {
    let { height, neck, waist, hip } = measurements;
    const ageVal = parseInt(age);
    
    // Validate inputs
    if (!height || !neck || !waist || (gender === 'female' && !hip)) {
      alert('Please fill in all required measurements');
      return;
    }
    if (!age || ageVal < 15 || ageVal > 100) {
      alert('Please enter a valid age (15-100)');
      return;
    }

    // Convert to numbers
    let heightIn = parseFloat(height);
    let neckIn = parseFloat(neck);
    let waistIn = parseFloat(waist);
    let hipIn = gender === 'female' ? parseFloat(hip) : 0;
    let weightLbs = weight ? parseFloat(weight) : null;

    // Convert metric to inches/lbs for the formula
    if (unit === 'metric') {
      heightIn = cmToInches(heightIn);
      neckIn = cmToInches(neckIn);
      waistIn = cmToInches(waistIn);
      hipIn = cmToInches(hipIn);
      if (weightLbs) weightLbs = kgToLbs(weightLbs);
    }

    let bodyFatPercentage;

    if (gender === 'male') {
      // Official US Navy formula for men (Hodgdon & Beckett, 1984)
      // BF% = 86.010 × log10(abdomen - neck) - 70.041 × log10(height) + 36.76
      bodyFatPercentage = 86.010 * Math.log10(waistIn - neckIn) - 70.041 * Math.log10(heightIn) + 36.76;
    } else {
      // Official US Navy formula for women (Hodgdon & Beckett, 1984)
      // BF% = 163.205 × log10(waist + hip - neck) - 97.684 × log10(height) - 78.387
      bodyFatPercentage = 163.205 * Math.log10(waistIn + hipIn - neckIn) - 97.684 * Math.log10(heightIn) - 78.387;
    }

    // Clamp to reasonable values
    bodyFatPercentage = Math.max(2, Math.min(60, bodyFatPercentage));

    // Age-adjusted category
    const { label, color, ageGroup } = getCategory(bodyFatPercentage, gender, ageVal);

    // Calculate body composition if weight is provided
    let bodyComp = null;
    if (weightLbs && weightLbs > 0) {
      const fatMassLbs = (bodyFatPercentage / 100) * weightLbs;
      const leanMassLbs = weightLbs - fatMassLbs;
      if (unit === 'metric') {
        const weightKg = parseFloat(weight);
        bodyComp = {
          fatMass: ((bodyFatPercentage / 100) * weightKg).toFixed(1),
          leanMass: (weightKg - (bodyFatPercentage / 100) * weightKg).toFixed(1),
          unit: 'kg'
        };
      } else {
        bodyComp = {
          fatMass: fatMassLbs.toFixed(1),
          leanMass: leanMassLbs.toFixed(1),
          unit: 'lbs'
        };
      }
    }

    setResult({
      percentage: bodyFatPercentage.toFixed(1),
      category: label,
      categoryColor: color,
      ageGroup,
      age: ageVal,
      bodyComp
    });
  };

  const clearForm = () => {
    setMeasurements({ height: '', neck: '', waist: '', hip: '' });
    setAge('');
    setWeight('');
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#F5F0EB]">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#1A1A1A]">
        <div className="container">
          <Link to="/resources" className="inline-flex items-center gap-2 text-[#C9A962] hover:text-white transition-colors mb-8">
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back to Resources</span>
          </Link>
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="text-[#C9A962]" size={32} />
              <span className="text-[#C9A962] text-sm font-semibold tracking-[0.1em] uppercase">Fitness Tool</span>
            </div>
            <h1 className="heading-display text-white mb-6">Body Fat Calculator</h1>
            <p className="text-lg md:text-xl text-white/90">
              Calculate your body fat percentage using the US Navy method. This formula uses your height and body circumference measurements.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 md:py-28">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-8 md:p-12 shadow-sm">
              {/* Gender Selection */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-3 tracking-wide uppercase">Gender</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setGender('male')}
                    className={`py-3 px-4 text-sm font-semibold tracking-wide uppercase transition-colors ${
                      gender === 'male'
                        ? 'bg-[#1A1A1A] text-white'
                        : 'bg-[#F5F0EB] text-[#1A1A1A] hover:bg-[#EDE8E3]'
                    }`}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => setGender('female')}
                    className={`py-3 px-4 text-sm font-semibold tracking-wide uppercase transition-colors ${
                      gender === 'female'
                        ? 'bg-[#1A1A1A] text-white'
                        : 'bg-[#F5F0EB] text-[#1A1A1A] hover:bg-[#EDE8E3]'
                    }`}
                  >
                    Female
                  </button>
                </div>
              </div>

              {/* Unit Selection */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-3 tracking-wide uppercase">Units</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setUnit('metric')}
                    className={`py-3 px-4 text-sm font-semibold tracking-wide uppercase transition-colors ${
                      unit === 'metric'
                        ? 'bg-[#1A1A1A] text-white'
                        : 'bg-[#F5F0EB] text-[#1A1A1A] hover:bg-[#EDE8E3]'
                    }`}
                  >
                    Metric (cm)
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnit('imperial')}
                    className={`py-3 px-4 text-sm font-semibold tracking-wide uppercase transition-colors ${
                      unit === 'imperial'
                        ? 'bg-[#1A1A1A] text-white'
                        : 'bg-[#F5F0EB] text-[#1A1A1A] hover:bg-[#EDE8E3]'
                    }`}
                  >
                    Imperial (in)
                  </button>
                </div>
              </div>

              {/* Age Input */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 tracking-wide uppercase">Age</label>
                <input
                  type="number"
                  data-testid="age-input"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="e.g. 30"
                  min="15"
                  max="100"
                  className="w-full px-4 py-3 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none transition-colors"
                />
                <p className="text-xs text-[#6B6B6B] mt-1">Body fat classification adjusts based on your age group</p>
              </div>

              {/* Measurements */}
              <div className="space-y-6 mb-8">
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 tracking-wide uppercase">
                    Weight ({unit === 'metric' ? 'kg' : 'lbs'}) — Optional
                  </label>
                  <input
                    type="number"
                    data-testid="weight-input"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder={unit === 'metric' ? 'e.g. 80' : 'e.g. 176'}
                    className="w-full px-4 py-3 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none transition-colors"
                  />
                  <p className="text-xs text-[#6B6B6B] mt-1">Used to calculate fat mass and lean mass breakdown</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 tracking-wide uppercase">
                    Height ({unit === 'metric' ? 'cm' : 'inches'})
                  </label>
                  <input
                    type="number"
                    name="height"
                    data-testid="height-input"
                    value={measurements.height}
                    onChange={handleInputChange}
                    placeholder={unit === 'metric' ? 'e.g. 175' : 'e.g. 69'}
                    className="w-full px-4 py-3 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 tracking-wide uppercase">
                    Neck Circumference ({unit === 'metric' ? 'cm' : 'inches'})
                  </label>
                  <input
                    type="number"
                    name="neck"
                    data-testid="neck-input"
                    value={measurements.neck}
                    onChange={handleInputChange}
                    placeholder={unit === 'metric' ? 'e.g. 38' : 'e.g. 15'}
                    className="w-full px-4 py-3 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none transition-colors"
                  />
                  <p className="text-xs text-[#6B6B6B] mt-1">Measure just below the larynx (Adam's apple), tape level and snug</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 tracking-wide uppercase">
                    {gender === 'male' ? 'Abdomen' : 'Waist'} Circumference ({unit === 'metric' ? 'cm' : 'inches'})
                  </label>
                  <input
                    type="number"
                    name="waist"
                    data-testid="waist-input"
                    value={measurements.waist}
                    onChange={handleInputChange}
                    placeholder={unit === 'metric' ? 'e.g. 85' : 'e.g. 33'}
                    className="w-full px-4 py-3 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none transition-colors"
                  />
                  <p className="text-xs text-[#6B6B6B] mt-1">
                    {gender === 'male' 
                      ? 'Measure horizontally at the navel. Do not suck in your stomach.' 
                      : 'Measure at the narrowest point of the natural waist'}
                  </p>
                </div>

                {gender === 'female' && (
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 tracking-wide uppercase">
                      Hip Circumference ({unit === 'metric' ? 'cm' : 'inches'})
                    </label>
                    <input
                      type="number"
                      name="hip"
                      data-testid="hip-input"
                      value={measurements.hip}
                      onChange={handleInputChange}
                      placeholder={unit === 'metric' ? 'e.g. 100' : 'e.g. 39'}
                      className="w-full px-4 py-3 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none transition-colors"
                    />
                    <p className="text-xs text-[#6B6B6B] mt-1">Measure at the widest point of hips/buttocks, feet together</p>
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <button
                  onClick={calculateBodyFat}
                  className="py-4 bg-[#C9A962] text-[#1A1A1A] text-sm font-semibold tracking-[0.1em] uppercase hover:bg-[#d4b872] transition-colors"
                >
                  Calculate
                </button>
                <button
                  onClick={clearForm}
                  className="py-4 bg-[#F5F0EB] text-[#1A1A1A] text-sm font-semibold tracking-[0.1em] uppercase hover:bg-[#EDE8E3] transition-colors"
                >
                  Clear
                </button>
              </div>

              {/* Result */}
              {result && (
                <div className="bg-[#1A1A1A] p-8 text-center" data-testid="bf-result">
                  <p className="text-[#C9A962] text-sm font-semibold tracking-[0.1em] uppercase mb-2">Your Body Fat Percentage</p>
                  <p className="text-5xl font-bold text-white mb-3" data-testid="bf-percentage" style={{fontFamily: "'Oswald', sans-serif"}}>
                    {result.percentage}%
                  </p>
                  <p className={`text-lg font-semibold ${result.categoryColor} mb-3`}>
                    {result.category}
                  </p>
                  {result.bodyComp && (
                    <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/10">
                      <div>
                        <p className="text-white/50 text-xs uppercase tracking-wide mb-1">Fat Mass</p>
                        <p className="text-white text-xl font-semibold" style={{fontFamily: "'Oswald', sans-serif"}}>
                          {result.bodyComp.fatMass} {result.bodyComp.unit}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/50 text-xs uppercase tracking-wide mb-1">Lean Mass</p>
                        <p className="text-white text-xl font-semibold" style={{fontFamily: "'Oswald', sans-serif"}}>
                          {result.bodyComp.leanMass} {result.bodyComp.unit}
                        </p>
                      </div>
                    </div>
                  )}
                  <p className="text-white/50 text-xs mt-4">
                    US Navy Method (Hodgdon & Beckett) — Age group {result.ageGroup} ({gender === 'male' ? 'Male' : 'Female'})
                  </p>
                </div>
              )}
            </div>

            {/* Body Fat Categories - Age Adjusted */}
            <div className="mt-12">
              <h2 className="heading-medium mb-2">Age-Adjusted Body Fat Categories</h2>
              <p className="text-sm text-[#6B6B6B] mb-6">Healthy body fat ranges shift with age as metabolism and fat distribution change. Categories below reflect age-adjusted standards.</p>
              
              {/* Men */}
              <div className="bg-white p-6 mb-6">
                <h3 className="heading-small mb-4">Men</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E5E0DB]">
                        <th className="text-left py-2 pr-2 text-[#6B6B6B] font-semibold">Category</th>
                        <th className="py-2 px-2 text-center font-semibold">18-29</th>
                        <th className="py-2 px-2 text-center font-semibold">30-39</th>
                        <th className="py-2 px-2 text-center font-semibold">40-49</th>
                        <th className="py-2 px-2 text-center font-semibold">50-59</th>
                        <th className="py-2 pl-2 text-center font-semibold">60+</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#E5E0DB]">
                        <td className="py-2 pr-2 text-[#6B6B6B]">Essential Fat</td>
                        <td className="py-2 px-2 text-center">2-5%</td>
                        <td className="py-2 px-2 text-center">2-5%</td>
                        <td className="py-2 px-2 text-center">2-5%</td>
                        <td className="py-2 px-2 text-center">2-5%</td>
                        <td className="py-2 pl-2 text-center">2-5%</td>
                      </tr>
                      <tr className="border-b border-[#E5E0DB]">
                        <td className="py-2 pr-2 text-[#6B6B6B]">Excellent</td>
                        <td className="py-2 px-2 text-center">6-13%</td>
                        <td className="py-2 px-2 text-center">6-14%</td>
                        <td className="py-2 px-2 text-center">6-16%</td>
                        <td className="py-2 px-2 text-center">6-17%</td>
                        <td className="py-2 pl-2 text-center">6-18%</td>
                      </tr>
                      <tr className="border-b border-[#E5E0DB]">
                        <td className="py-2 pr-2 text-[#6B6B6B]">Good</td>
                        <td className="py-2 px-2 text-center">14-17%</td>
                        <td className="py-2 px-2 text-center">15-18%</td>
                        <td className="py-2 px-2 text-center">17-20%</td>
                        <td className="py-2 px-2 text-center">18-21%</td>
                        <td className="py-2 pl-2 text-center">19-22%</td>
                      </tr>
                      <tr className="border-b border-[#E5E0DB]">
                        <td className="py-2 pr-2 text-[#6B6B6B]">Average</td>
                        <td className="py-2 px-2 text-center">18-22%</td>
                        <td className="py-2 px-2 text-center">19-23%</td>
                        <td className="py-2 px-2 text-center">21-25%</td>
                        <td className="py-2 px-2 text-center">22-27%</td>
                        <td className="py-2 pl-2 text-center">23-28%</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-2 text-[#6B6B6B]">Above Average</td>
                        <td className="py-2 px-2 text-center">23%+</td>
                        <td className="py-2 px-2 text-center">24%+</td>
                        <td className="py-2 px-2 text-center">26%+</td>
                        <td className="py-2 px-2 text-center">28%+</td>
                        <td className="py-2 pl-2 text-center">29%+</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Women */}
              <div className="bg-white p-6">
                <h3 className="heading-small mb-4">Women</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E5E0DB]">
                        <th className="text-left py-2 pr-2 text-[#6B6B6B] font-semibold">Category</th>
                        <th className="py-2 px-2 text-center font-semibold">18-29</th>
                        <th className="py-2 px-2 text-center font-semibold">30-39</th>
                        <th className="py-2 px-2 text-center font-semibold">40-49</th>
                        <th className="py-2 px-2 text-center font-semibold">50-59</th>
                        <th className="py-2 pl-2 text-center font-semibold">60+</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#E5E0DB]">
                        <td className="py-2 pr-2 text-[#6B6B6B]">Essential Fat</td>
                        <td className="py-2 px-2 text-center">10-13%</td>
                        <td className="py-2 px-2 text-center">10-13%</td>
                        <td className="py-2 px-2 text-center">10-13%</td>
                        <td className="py-2 px-2 text-center">10-13%</td>
                        <td className="py-2 pl-2 text-center">10-13%</td>
                      </tr>
                      <tr className="border-b border-[#E5E0DB]">
                        <td className="py-2 pr-2 text-[#6B6B6B]">Excellent</td>
                        <td className="py-2 px-2 text-center">14-20%</td>
                        <td className="py-2 px-2 text-center">14-21%</td>
                        <td className="py-2 px-2 text-center">14-23%</td>
                        <td className="py-2 px-2 text-center">14-24%</td>
                        <td className="py-2 pl-2 text-center">14-25%</td>
                      </tr>
                      <tr className="border-b border-[#E5E0DB]">
                        <td className="py-2 pr-2 text-[#6B6B6B]">Good</td>
                        <td className="py-2 px-2 text-center">21-24%</td>
                        <td className="py-2 px-2 text-center">22-25%</td>
                        <td className="py-2 px-2 text-center">24-27%</td>
                        <td className="py-2 px-2 text-center">25-28%</td>
                        <td className="py-2 pl-2 text-center">26-29%</td>
                      </tr>
                      <tr className="border-b border-[#E5E0DB]">
                        <td className="py-2 pr-2 text-[#6B6B6B]">Average</td>
                        <td className="py-2 px-2 text-center">25-31%</td>
                        <td className="py-2 px-2 text-center">26-32%</td>
                        <td className="py-2 px-2 text-center">28-34%</td>
                        <td className="py-2 px-2 text-center">29-35%</td>
                        <td className="py-2 pl-2 text-center">30-36%</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-2 text-[#6B6B6B]">Above Average</td>
                        <td className="py-2 px-2 text-center">32%+</td>
                        <td className="py-2 px-2 text-center">33%+</td>
                        <td className="py-2 px-2 text-center">35%+</td>
                        <td className="py-2 px-2 text-center">36%+</td>
                        <td className="py-2 pl-2 text-center">37%+</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Age & Body Fat Context */}
            <div className="mt-8 bg-white p-8">
              <div className="flex items-start gap-3 mb-4">
                <Info className="text-[#C9A962] mt-0.5 shrink-0" size={20} />
                <h2 className="heading-medium">How Age Affects Body Fat</h2>
              </div>
              <div className="space-y-3 text-sm text-[#6B6B6B]">
                <p>As you age, your body naturally redistributes and stores fat differently. Metabolic rate decreases, lean muscle mass declines, and hormonal shifts (particularly testosterone in men and oestrogen in women) affect where and how much fat your body retains.</p>
                <p>This is why the same body fat percentage can be classified differently for a 25-year-old versus a 50-year-old. The categories above reflect age-adjusted standards used by fitness professionals worldwide.</p>
              </div>
            </div>

            {/* How to Measure */}
            <div className="mt-12 bg-white p-8">
              <h2 className="heading-medium mb-6">How to Measure</h2>
              <p className="text-sm text-[#6B6B6B] mb-4">Take each measurement 3 times and use the average. Use a flexible tape measure, held snug but not compressing the skin.</p>
              <div className="space-y-4 text-sm text-[#6B6B6B]">
                <div className="flex gap-4">
                  <span className="text-[#C9A962] font-bold shrink-0">Neck:</span>
                  <p>Measure the circumference just below the larynx (Adam's apple). Keep the tape level, head straight, shoulders relaxed. Do not flex your neck.</p>
                </div>
                <div className="flex gap-4">
                  <span className="text-[#C9A962] font-bold shrink-0">Abdomen (Men):</span>
                  <p>Measure horizontally at the navel (belly button). Exhale normally — do not suck in your stomach. This is NOT the narrowest part of your waist.</p>
                </div>
                <div className="flex gap-4">
                  <span className="text-[#C9A962] font-bold shrink-0">Waist (Women):</span>
                  <p>Measure at the narrowest point of the natural waist, typically above the hip bone and below the rib cage.</p>
                </div>
                <div className="flex gap-4">
                  <span className="text-[#C9A962] font-bold shrink-0">Hip (Women):</span>
                  <p>Measure at the widest point of your hips/buttocks while standing with feet together.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BodyFatCalculator;
